import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Globe, Trash2, Edit } from 'lucide-react';
import { getOrganizations, deleteOrganization } from '../../../lib/supabase/contacts';
import { useEntity } from '../../../hooks/useEntity';
import DeleteConfirmationModal from '../../ui/DeleteConfirmationModal';
import type { Organization } from '../../../types/contacts';

interface OrganizationListProps {
  onEditOrganization?: (organization: Organization) => void;
  refreshTrigger?: number;
  refreshTrigger?: number;
}

export default function OrganizationList({ onEditOrganization, refreshTrigger = 0 }: OrganizationListProps) {
  const { entity } = useEntity();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState<Organization | null>(null);

  const loadOrganizations = useCallback(async () => {
    if (!entity?.id) {
      setError('No entity selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const orgs = await getOrganizations(entity.id);
      setOrganizations(orgs || []);
    } catch (err) {
      console.error('Failed to load organizations:', err);
      setError('Failed to load organizations. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [entity?.id, refreshTrigger]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations, refreshTrigger]);
  
  const confirmDelete = (organization: Organization) => {
    setOrgToDelete(organization);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!orgToDelete) return;
    
    try {
      setDeleteLoading(orgToDelete.id);
      await deleteOrganization(orgToDelete.id);
      setOrganizations(organizations.filter(org => org.id !== orgToDelete.id));
      setShowDeleteModal(false);
      setOrgToDelete(null);
    } catch (err) {
      console.error('Failed to delete organization:', err);
      setError('Failed to delete organization. Please try again.');
    } finally {
      setDeleteLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
        <p className="text-gray-500 mt-2">Loading organizations...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        <p>{error}</p>
        <button
          onClick={loadOrganizations}
          className="mt-2 text-blue-500 hover:text-blue-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {organizations.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Building2 className="w-12 h-12 mx-auto text-gray-400 mb-2" />
          <p>No organizations yet. Add your first organization to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {organizations.map((org) => (
            <div
              key={org.id}
              className="bg-white rounded-lg border border-gray-100 p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-50 p-2 rounded-full">
                    <Building2 className="w-5 h-5 text-blue-500" />
                  </div>
                  <h3 className="font-medium text-gray-900">{org.name}</h3>
                </div>
                <div className="flex items-center space-x-2">
                  <button 
                    className="text-gray-400 hover:text-blue-500"
                    onClick={() => onEditOrganization && onEditOrganization(org)}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button 
                    className="text-gray-400 hover:text-red-500"
                    onClick={() => confirmDelete(org)}
                    disabled={deleteLoading === org.id}
                  >
                    {deleteLoading === org.id ? (
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
              
              {org.domain && (
                <div className="mt-3 flex items-center text-sm text-gray-600">
                  <Globe className="w-4 h-4 mr-2" />
                  <span>{org.domain}</span>
                </div>
              )}
              <div className="mt-2 flex items-center text-sm text-gray-600">
                <div className="px-2 py-1 bg-blue-50 rounded text-blue-700 text-xs capitalize">
                  {org.type}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {showDeleteModal && (
        <DeleteConfirmationModal
          title="Delete Organization"
          message="Are you sure you want to delete this organization? This action cannot be undone."
          isDeleting={!!deleteLoading}
          onConfirm={handleDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setOrgToDelete(null);
          }}
        />
      )}
    </div>
  );
}